import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { minorToMajor } from '@/lib/quoteMoney';
import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_API_URL;

export function MasterCatalogPicker({ sectorKey, disabled, onAdd, lang = 'tr' }) {
  const [paths, setPaths] = useState([]);
  const [systemKey, setSystemKey] = useState('');
  const [subsystemKey, setSubsystemKey] = useState('');
  const [families, setFamilies] = useState([]);
  const [familyId, setFamilyId] = useState('');
  const [familyDetail, setFamilyDetail] = useState(null);
  const [variantId, setVariantId] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPaths([]);
    setSystemKey('');
    setSubsystemKey('');
    setFamilies([]);
    setFamilyId('');
    setFamilyDetail(null);
    setVariantId('');
    if (!sectorKey) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/quote/master-catalog/systems`, {
          params: { sector_key: sectorKey, locale: lang },
          withCredentials: true,
        });
        if (!cancelled) setPaths(data.items || []);
      } catch (err) {
        if (!cancelled) {
          setPaths([]);
          toast.error(err.response?.data?.detail || (lang === 'en' ? 'Master Catalog systems could not be loaded.' : 'Master katalog sistemleri yüklenemedi'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sectorKey, lang]);

  const systems = useMemo(() => {
    const map = new Map();
    paths.forEach((row) => {
      if (row.system_key && !map.has(row.system_key)) {
        map.set(
          row.system_key,
          row.display_system_name || row.system_name || row.system_key,
        );
      }
    });
    return [...map.entries()].map(([key, name]) => ({ key, name }));
  }, [paths, lang]);

  const subsystems = useMemo(() => {
    const map = new Map();
    paths
      .filter((row) => row.system_key === systemKey)
      .forEach((row) => {
        if (row.subsystem_key && !map.has(row.subsystem_key)) {
          map.set(
            row.subsystem_key,
            row.display_subsystem_name || row.subsystem_name || row.subsystem_key,
          );
        }
      });
    return [...map.entries()].map(([key, name]) => ({ key, name }));
  }, [paths, systemKey, lang]);

  useEffect(() => {
    if (!sectorKey) return;
    const q = query.trim();
    if (!q && (!systemKey || !subsystemKey)) {
      setFamilies([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { sector_key: sectorKey, locale: lang, page_size: 100 };
        if (q) params.q = q;
        else {
          params.system_key = systemKey;
          params.subsystem_key = subsystemKey;
        }
        const { data } = await axios.get(`${API_URL}/api/quote/master-catalog/families`, {
          params,
          withCredentials: true,
        });
        setFamilies(data.items || []);
      } catch (err) {
        setFamilies([]);
        toast.error(err.response?.data?.detail || (lang === 'en' ? 'Master Catalog product families could not be loaded.' : 'Master katalog ürün aileleri yüklenemedi'));
      } finally {
        setLoading(false);
      }
    }, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [sectorKey, systemKey, subsystemKey, query, lang]);

  useEffect(() => {
    setFamilyDetail(null);
    setVariantId('');
    if (!familyId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/quote/master-catalog/families/${encodeURIComponent(familyId)}`,
          {
            params: { sector_key: sectorKey, locale: lang },
            withCredentials: true,
          },
        );
        if (!cancelled) setFamilyDetail(data);
      } catch (err) {
        if (!cancelled) {
          setFamilyDetail(null);
          toast.error(err.response?.data?.detail || (lang === 'en' ? 'Master Catalog variants could not be loaded.' : 'Master katalog varyantları yüklenemedi'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [familyId, sectorKey, lang]);

  const selectedVariant = (familyDetail?.variants || []).find((v) => v.variant_id === variantId);
  const canAdd = !!familyDetail && ((familyDetail.variants || []).length === 0 || !!selectedVariant);

  const add = () => {
    if (!canAdd) return;
    onAdd({
      master_family_id: familyDetail.family_id,
      master_variant_id: selectedVariant?.variant_id || null,
      master_catalog_revision: familyDetail.catalog_revision,
      item_type: familyDetail.item_type || 'product',
      name: selectedVariant
        ? `${familyDetail.display_name || familyDetail.name} — ${selectedVariant.display_name || selectedVariant.name}`
        : (familyDetail.display_name || familyDetail.name),
      description: [
        familyDetail.display_system_name || familyDetail.system_name,
        familyDetail.display_subsystem_name || familyDetail.subsystem_name,
      ].filter(Boolean).join(' / '),
      unit: familyDetail.display_unit || familyDetail.default_unit || (lang === 'en' ? 'pcs' : 'adet'),
      technical_attributes: selectedVariant?.technical_attributes || {},
      unit_price_major: Number(selectedVariant?.unit_price_minor || 0) > 0
        ? minorToMajor(selectedVariant.unit_price_minor).toString()
        : '',
      cost_price_major: Number(selectedVariant?.cost_price_minor || 0) > 0
        ? minorToMajor(selectedVariant.cost_price_minor).toString()
        : '',
      price_unset: Number(selectedVariant?.unit_price_minor || 0) <= 0,
    });
    setFamilyId('');
    setFamilyDetail(null);
    setVariantId('');
  };

  return (
    <div className="w-full rounded-xl border border-[#E5E5E5] bg-[#FAFAFA] p-3" data-testid="master-catalog-picker">
      <div className="flex items-center gap-2 mb-3">
        <Search className="w-4 h-4 text-[#64748B]" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setFamilyId('');
            setFamilyDetail(null);
            setVariantId('');
          }}
          placeholder={lang === 'en'
            ? 'Search Master Catalog (e.g. NVR, Cat6, fire panel)'
            : 'Master katalogda ara (örn. NVR, Cat6, yangın paneli)'}
          disabled={disabled || !sectorKey}
          data-testid="master-catalog-search"
        />
      </div>

      {!query.trim() && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <Select
            value={systemKey}
            onValueChange={(value) => {
              setSystemKey(value);
              setSubsystemKey('');
              setFamilyId('');
              setFamilyDetail(null);
            }}
            disabled={disabled || !sectorKey}
          >
            <SelectTrigger><SelectValue placeholder={lang === 'en' ? 'System' : 'Sistem'} /></SelectTrigger>
            <SelectContent>
              {systems.map((row) => <SelectItem key={row.key} value={row.key}>{row.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select
            value={subsystemKey}
            onValueChange={(value) => {
              setSubsystemKey(value);
              setFamilyId('');
              setFamilyDetail(null);
            }}
            disabled={disabled || !systemKey}
          >
            <SelectTrigger><SelectValue placeholder={lang === 'en' ? 'Subsystem' : 'Alt sistem'} /></SelectTrigger>
            <SelectContent>
              {subsystems.map((row) => <SelectItem key={row.key} value={row.key}>{row.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select
            value={familyId}
            onValueChange={setFamilyId}
            disabled={disabled || loading || families.length === 0}
          >
            <SelectTrigger><SelectValue placeholder={loading
              ? (lang === 'en' ? 'Loading...' : 'Yükleniyor...')
              : (lang === 'en' ? 'Product / service family' : 'Ürün / hizmet ailesi')} /></SelectTrigger>
            <SelectContent>
              {families.map((row) => (
                <SelectItem key={row.family_id} value={row.family_id}>
                  {query.trim()
                    ? `${row.display_system_name || row.system_name} / ${row.display_name || row.name}`
                    : (row.display_name || row.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {query.trim() && (
        <div className="mb-2">
          <Select
            value={familyId}
            onValueChange={setFamilyId}
            disabled={disabled || loading || families.length === 0}
          >
            <SelectTrigger><SelectValue placeholder={loading
              ? (lang === 'en' ? 'Searching...' : 'Aranıyor...')
              : (lang === 'en' ? 'Select search result' : 'Arama sonucu seç')} /></SelectTrigger>
            <SelectContent>
              {families.map((row) => (
                <SelectItem key={row.family_id} value={row.family_id}>
                  {row.display_system_name || row.system_name}
                  {' / '}
                  {row.display_subsystem_name || row.subsystem_name}
                  {' / '}
                  {row.display_name || row.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {familyDetail && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
          {(familyDetail.variants || []).length > 0 ? (
            <Select value={variantId} onValueChange={setVariantId} disabled={disabled}>
              <SelectTrigger data-testid="master-catalog-variant">
                <SelectValue placeholder={lang === 'en' ? 'Select variant' : 'Varyant seç'} />
              </SelectTrigger>
              <SelectContent>
                {(familyDetail.variants || []).map((row) => (
                  <SelectItem key={row.variant_id} value={row.variant_id}>
                    {row.display_name || row.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="text-sm text-[#64748B] px-1 py-2">
              {lang === 'en'
                ? `${familyDetail.display_name || familyDetail.name} — family without variants`
                : `${familyDetail.display_name || familyDetail.name} — varyantsız aile`}
            </div>
          )}
          <Button type="button" onClick={add} disabled={disabled || !canAdd}>
            <Plus className="w-4 h-4 mr-1" /> {lang === 'en' ? 'Add to Quote' : 'Teklife Ekle'}
          </Button>
        </div>
      )}
    </div>
  );
}
